import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";
import { useSelector } from "react-redux";
import * as DocumentPicker from "expo-document-picker";
import { every, some } from "lodash";

import { tProductsBulkRequestBody } from "tmt_api/src/types/types.validator";

import * as orderDb from "src/db/order.db";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { useAppDispatch } from "src/redux";
import { SLImage } from "src/components";
import { PdAdd, PdChevronRight, PdCopy, PdImport, PdSettings, PdShopify } from "src/components/Icons";
import { PdStack, PdText } from "src/components/layout";
import {
  PdBadge,
  PdBadgeLabel,
  PdTab,
  PdTabLabel,
  PdTable,
  PdTableCell,
  PdTableCellText,
  PdTableRow,
  PdTableSortableHeaders,
} from "src/components/v2/layout";
import { PdSearchInput } from "src/components/v2/ui";
import { tColumn, useTable } from "src/hooks/table";
import { useIsMobile } from "src/hooks/ui";
import { generateCSVFromTableInstance, getHiddenColumnsFromConfig } from "src/util/table";
import React from "react";
import { formatMoney } from "src/util/gFormatting";
import { PdInputFloat, PdInputGroup, PdInputLeftAddon, PdInputNumber } from "src/components/v2/forms/input";
import { PdButton, PdButtonLabel } from "src/components/v2/forms/button";
import { bulkEdit } from "src/redux/products/thunkActions";
import { NavigationProp, ParamListBase } from "@react-navigation/native";
import { kRoute } from "src/kRoute";
import { useBusyModal, useConfirmModal, useShopifySettingsModal, useVendorSetupShopifyModal } from "src/hooks/modals";
import { bulkUploadProducts } from "src/redux/company/actions";
import { tFilters, tProductStatus, tTableProduct, tUpdated, tUpdatedPayload } from "./types";
import { getColorSchemeFromStatus, getInventory, getMsrpPrice, getTesterPrice, getWholesalePrice } from "./utils";
import { useReviewChangesBeforeLeave, useTableData } from "./hooks";
import { tPressableState } from "src/components/types";
import { PdFormLabel } from "src/components/v2/forms/control";
import { PdCheckbox, PdCheckboxLabel } from "src/components/v2/forms/checkbox";
import { PdRow } from "src/components/v2/ui/core";
import { PdPopover, PdPopoverContent, PdPopoverTrigger } from "src/components/v2/overlay";
import PopoverContentTableColumns from "src/components/connected/PopoverContentTableColumns";

const productSheetMimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const P08_Products: React.FC<{ navigation: NavigationProp<ParamListBase> }> = ({ navigation }) => {
  const busyModal = useBusyModal();
  const confirmModal = useConfirmModal();
  const shopifySettingsModal = useShopifySettingsModal();
  const vendorSetupShopifyModal = useVendorSetupShopifyModal();
  const dispatch = useAppDispatch();
  const rIsLoading = useSelector(
    (state) => state.LoadingReducer.productFetching || state.LoadingReducer.companyFetching,
  );
  const rIsShopifyFeatureFlagOn = useSelector(
    (state) => state.CompanyReducer.type === "brand" && state.CompanyReducer.shopifyFeatureFlag,
  );
  const rIsLinkedToShopify = useSelector(
    (state) => state.CompanyReducer.type === "brand" && !!state.CompanyReducer.shopifySessionId,
  );
  const rTableConfigs = useSelector((state) => state.TableConfigReducer.inventoryManagament);
  const [filters, setFilters] = React.useState<tFilters>({
    status: undefined,
    term: "",
    sortedBy: "name",
    sortDirection: "asc",
  });
  const { data, totals } = useTableData(filters);
  const [updated, setUpdated] = React.useState<tUpdated>({});
  const [isSaving, setSaving] = React.useState(false);
  const [isPublishing, setPublishing] = React.useState(false);
  const [isUnpublishing, setUnpublishing] = React.useState(false);
  const [isMarkingOos, setMarkingOos] = React.useState(false);
  const [isBulkPublishing, setBulkPublising] = React.useState(false);
  const [isBulkUnpublishing, setBulkUnpublising] = React.useState(false);
  const [isBulkMarkingOos, setBulkMarkingOos] = React.useState(false);
  const isAllSelected = React.useMemo(
    () =>
      data.length > 0 &&
      Object.keys(updated).length === data.reduce((total, d) => (total += (d.subRows?.length ?? 0) + 1), 0) &&
      every(updated, (u) => u.isSelected === true),
    [data, updated],
  );
  const isSomeSelected = React.useMemo(
    () => Object.keys(updated).length > 0 && some(updated, (u) => u.isSelected === true),
    [updated],
  );
  const isSomeUpdated = React.useMemo(
    () =>
      some(
        updated,
        (u) =>
          u.inventory !== undefined ||
          u.wholesalePrice !== undefined ||
          u.msrpPrice !== undefined ||
          u.testerPrice !== undefined,
      ),
    [updated],
  );

  const clearEditValues = () => {
    setUpdated((prev) => {
      const next: tUpdated = {};
      for (const key of Object.keys(prev)) {
        next[key] = {
          ...prev[key],
          inventory: undefined,
          wholesalePrice: undefined,
          msrpPrice: undefined,
          testerPrice: undefined,
          currentInventory: undefined,
          hasVariants: undefined,
        };
      }
      return next;
    });
  };

  const handleOnPressToggleAll = React.useCallback(
    (isSelected: boolean) => {
      setUpdated((prev) => {
        const next: tUpdated = { ...prev };
        for (const row of data) {
          next[row.id] = {
            ...prev[row.id],
            productId: row.uid,
            variantSku: row.isVariant ? row.sku : undefined,
            isSelected,
            currentInventory: row.inventory,
            hasVariants: row.hasVariants,
          };

          for (const subRow of row.subRows ?? []) {
            next[subRow.id] = {
              ...prev[subRow.id],
              productId: subRow.uid,
              variantSku: subRow.isVariant ? subRow.sku : undefined,
              isSelected,
              currentInventory: subRow.inventory,
              hasVariants: false,
            };
          }
        }
        return next;
      });
    },
    [data],
  );

  const updateRow = React.useCallback((row: tTableProduct, payload: Partial<tUpdatedPayload>) => {
    setUpdated((prev) => {
      const next: tUpdated = {
        ...prev,
        [row.id]: {
          ...prev[row.id],
          productId: row.uid,
          variantSku: row.isVariant ? row.sku : undefined,
          currentInventory: row.inventory,
          hasVariants: row.hasVariants,
          ...payload,
        },
      };

      for (const subRow of row.subRows ?? []) {
        next[subRow.id] = {
          ...prev[subRow.id],
          productId: subRow.uid,
          variantSku: subRow.isVariant ? subRow.sku : undefined,
          currentInventory: row.inventory,
          hasVariants: row.hasVariants,
          ...payload,
        };
      }

      return next;
    });
  }, []);

  const columns: tColumn<tTableProduct>[] = React.useMemo(
    () => [
      {
        id: "id",
        header: () => (
          <PdCheckbox
            value={isAllSelected}
            onChange={(value) => handleOnPressToggleAll(value)}
            style={styles.alignEnd}
          />
        ),
        accessor: (originalRow) => originalRow.id,
        Cell: ({ row }) => (
          <PdCheckbox
            value={!!updated[row.id]?.isSelected}
            onChange={(value) => updateRow(row, { isSelected: value })}
          />
        ),
        isSortable: false,
        isHideable: false,
        style: [styles.tableColumnXs],
      },
      {
        id: "name",
        header: "Product",
        accessor: (originalRow) => originalRow.name,
        Cell: ({ value, row, depth, style }) => (
          <Pressable onPress={() => navigation.navigate(kRoute.P08_01, { id: row.uid })}>
            {({ pressed, hovered }: tPressableState) => (
              <PdTableCell
                style={[
                  styles.flexRow,
                  styles.alignCenter,
                  depth ? { paddingLeft: depth * 6 } : {},
                  hovered ? styles.rowHovered : {},
                  pressed ? styles.rowPressed : {},
                  style,
                ]}
              >
                {!!depth && (
                  <View style={[styles.sizeXxs, styles.mr2]}>
                    <PdChevronRight color={kColors.gray500} />
                  </View>
                )}
                <SLImage source={{ uri: row.image }} mobileStyle={styles.image} webStyle={webStyles.image} />
                <PdTableCellText style={[styles.maxW400, styles.ml2]}>{value}</PdTableCellText>
              </PdTableCell>
            )}
          </Pressable>
        ),
        style: styles.tableColumnXl,
      },
      {
        id: "sku",
        header: "Sku",
        accessor: (originalRow) => originalRow.sku,
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnLg,
      },
      {
        id: "status",
        header: "SKU Status",
        accessor: (originalRow) => originalRow.status,
        Cell: ({ value, row }) =>
          row.hasVariants ? (
            <PdText>-</PdText>
          ) : (
            <PdBadge colorScheme={getColorSchemeFromStatus(row.status)}>
              <PdBadgeLabel>{value}</PdBadgeLabel>
            </PdBadge>
          ),
        style: styles.tableColumnMd,
      },
      {
        id: "inventory",
        header: "Current Inventory",
        accessor: (originalRow) => originalRow.inventory,
        Cell: ({ row, value }) => (row.hasVariants ? <PdText>-</PdText> : <PdTableCellText>{value}</PdTableCellText>),
        style: styles.tableColumnSm,
      },
      {
        id: "updateInventory",
        header: "Update Inventory",
        accessor: (originalRow) => originalRow.inventory,
        Cell: ({ row }) =>
          row.hasVariants ? (
            <PdText>-</PdText>
          ) : (
            <PdInputNumber
              value={getInventory(row, updated)}
              onChangeNumber={(value) => updateRow(row, { inventory: value || 0 })}
              style={styles.widthFull}
            />
          ),
        style: styles.tableColumnSm,
        isSortable: false,
      },
      {
        id: "wholesalePrice",
        header: "Wholesale",
        accessor: (originalRow) => formatMoney(originalRow.wholesalePrice),
        Cell: ({ row, value }) => (row.hasVariants ? <PdText>-</PdText> : <PdTableCellText>{value}</PdTableCellText>),
        style: styles.tableColumnSm,
      },
      {
        id: "updateWholesalePrice",
        header: "Update WSP",
        accessor: (originalRow) => originalRow.wholesalePrice,
        Cell: ({ row }) =>
          row.hasVariants ? (
            <PdText>-</PdText>
          ) : (
            <PdInputGroup style={styles.widthFull}>
              <PdInputLeftAddon>$</PdInputLeftAddon>
              <PdInputFloat
                value={getWholesalePrice(row, updated)}
                onChangeText={(value) => updateRow(row, { wholesalePrice: value })}
                style={styles.pdInputGroupInput}
              />
            </PdInputGroup>
          ),
        style: styles.tableColumnSm,
        isSortable: false,
      },
      {
        id: "msrpPrice",
        header: "MSRP",
        accessor: (originalRow) => formatMoney(originalRow.msrpPrice),
        Cell: ({ row, value }) => (row.hasVariants ? <PdText>-</PdText> : <PdTableCellText>{value}</PdTableCellText>),
        style: styles.tableColumnSm,
      },
      {
        id: "updateMsrpPrice",
        header: "Update MSRP",
        accessor: (originalRow) => originalRow.msrpPrice,
        Cell: ({ row }) =>
          row.hasVariants ? (
            <PdText>-</PdText>
          ) : (
            <PdInputGroup style={styles.widthFull}>
              <PdInputLeftAddon>$</PdInputLeftAddon>
              <PdInputFloat
                value={getMsrpPrice(row, updated)}
                onChangeText={(value) => updateRow(row, { msrpPrice: value })}
                style={styles.pdInputGroupInput}
              />
            </PdInputGroup>
          ),
        style: styles.tableColumnSm,
        isSortable: false,
      },
      {
        id: "testerPrice",
        header: "Tester",
        accessor: (originalRow) => originalRow.testerPrice,
        Cell: ({ row }) =>
          row.hasVariants ? (
            <PdText>-</PdText>
          ) : row.hasTester ? (
            <PdInputGroup style={styles.widthFull}>
              <PdInputLeftAddon>$</PdInputLeftAddon>
              <PdInputFloat
                value={getTesterPrice(row, updated)}
                onChangeText={(value) => updateRow(row, { testerPrice: value })}
                style={styles.pdInputGroupInput}
              />
            </PdInputGroup>
          ) : (
            <PdText>-</PdText>
          ),
        style: styles.tableColumnSm,
        isSortable: false,
      },
    ],
    [handleOnPressToggleAll, isAllSelected, navigation, updateRow, updated],
  );

  const tableInstance = useTable<tTableProduct>({
    data,
    columns,
    initialState: {
      sortedBy: filters.sortedBy,
      sortDirection: filters.sortDirection,
      hiddenColumns: getHiddenColumnsFromConfig(rTableConfigs),
    },
  });
  const isMobile = useIsMobile();

  const handleOnAddProduct = () => {
    navigation.navigate(kRoute.P08_01, { id: "" });
  };

  const handleOnImportProducts = async () => {
    const documentResult = await DocumentPicker.getDocumentAsync({ type: productSheetMimeType });
    const modalId = busyModal.open();

    try {
      await dispatch(bulkUploadProducts(documentResult));
    } finally {
      busyModal.close(modalId);
    }
  };

  const handleOnPressShopify = () => {
    if (rIsLinkedToShopify) {
      shopifySettingsModal.openAsync();
    } else {
      vendorSetupShopifyModal.openAsync();
    }
  };

  const handleOnPressCopyAsCSV = async () => {
    try {
      await navigator.clipboard.writeText(generateCSVFromTableInstance(tableInstance.rows, tableInstance.headers));
      dispatch({ type: "RIBBON", message: "Copied to clipboard" });
    } catch (er) {
      dispatch({ type: "RIBBON", message: new Error("Could not copy to clipboard") });
    }
  };

  const updateHiddenColumns = (newTableConfig: Record<keyof tTableProduct, boolean>) => {
    const newHiddenColumns = getHiddenColumnsFromConfig(newTableConfig);
    tableInstance.setHiddenColumns(newHiddenColumns);
  };

  const handleOnPressSaveChanges = React.useCallback(async () => {
    setSaving(true);
    const payload: tProductsBulkRequestBody = [];
    for (const values of Object.values(updated)) {
      payload.push({
        productId: values.productId,
        variantSku: values.variantSku,
        inventory: values.inventory,
        wholesalePrice: values.wholesalePrice ? Number.parseFloat(values.wholesalePrice) : undefined,
        msrpPrice: values.msrpPrice ? Number.parseFloat(values.msrpPrice) : undefined,
        testerPrice: values.testerPrice ? Number.parseFloat(values.testerPrice) : undefined,
      });
    }
    await dispatch(bulkEdit(payload));
    clearEditValues();
    setSaving(false);
  }, [dispatch, updated]);

  const handleOnPressBulkPublish = async () => {
    setPublishing(true);

    const response = await confirmModal.openAsync({
      title: "PUBLISH PRODUCTS",
      description: "All selected products will be published & visible in your storefront.",
    });
    if (response.status === "closed") {
      setPublishing(false);
      return;
    }

    let isSomeProductOos = false;
    const payload: tProductsBulkRequestBody = [];
    for (const values of Object.values(updated)) {
      if (values.isSelected) {
        payload.push({ productId: values.productId, variantSku: values.variantSku, publish: true });
        isSomeProductOos = isSomeProductOos || (!values.hasVariants && values.currentInventory === 0);
      }
    }

    if (isSomeProductOos) {
      await confirmModal.openAsync({
        title: "Out Of Stock Items",
        description:
          "To publish and reactivate out of stock items, please first update the inventory quantity available and click Save.",
        confirmButtonLabel: "OK",
        hideCancelButton: true,
      });
      setPublishing(false);
      return;
    }

    try {
      setBulkPublising(true);
      await dispatch(bulkEdit(payload));
    } finally {
      handleOnPressToggleAll(false);
      setPublishing(false);
      setBulkPublising(false);
    }
  };

  const handleOnPressBulkUnpublish = async () => {
    setUnpublishing(true);

    let isProductInSomePendingOrder = false;
    const payload: tProductsBulkRequestBody = [];
    const pendingOrdersQuery = await orderDb.queryOrder({ status: "Pending" }).get();
    const productIds = [];

    for (const values of Object.values(updated)) {
      if (values.isSelected) {
        payload.push({ productId: values.productId, variantSku: values.variantSku, publish: false });
        productIds.push(values.productId);

        isProductInSomePendingOrder =
          isProductInSomePendingOrder ||
          some(pendingOrdersQuery.docs, (document) => document.data().cart.productIds.includes(values.productId));
      }
    }

    if (isProductInSomePendingOrder) {
      const response = await confirmModal.openAsync({
        title: "INVENTORY NOTICE",
        description: () => (
          <>
            <PdText style={styles.mb4}>
              Please note you have one or more open orders in Pending stage that include the product you are trying to
              Unpublish.
            </PdText>
            <PdText style={styles.mb10}>
              If you proceed, the product will be automatically removed from the open orders.
            </PdText>
          </>
        ),
      });
      if (response.status === "closed") {
        setUnpublishing(false);
        return;
      }
    } else {
      const response = await confirmModal.openAsync({
        title: "UNPUBLISH PRODUCTS",
        description: "All selected products will be unpublished & hidden in your storefront.",
      });
      if (response.status === "closed") {
        setUnpublishing(false);
        return;
      }
    }

    try {
      setBulkUnpublising(true);
      await dispatch(bulkEdit(payload));
    } finally {
      handleOnPressToggleAll(false);
      setUnpublishing(false);
      setBulkUnpublising(false);
    }
  };

  const handleOnPressOutOfStock = async () => {
    setMarkingOos(true);

    const response = await confirmModal.openAsync({
      title: "MARK OUT OF STOCK",
      description:
        "All selected products will be marked 'Out Of Stock' in your storefront & retailers will not be able to purchase them.",
    });
    if (response.status === "closed") {
      setMarkingOos(false);
      return;
    }

    const payload: tProductsBulkRequestBody = [];
    for (const values of Object.values(updated)) {
      if (values.isSelected) {
        payload.push({ productId: values.productId, variantSku: values.variantSku, inventory: 0 });
      }
    }

    try {
      setBulkMarkingOos(true);
      await dispatch(bulkEdit(payload));
    } finally {
      handleOnPressToggleAll(false);
      clearEditValues();
      setMarkingOos(false);
      setBulkMarkingOos(false);
    }
  };

  useReviewChangesBeforeLeave({ enabled: isSomeUpdated, confirmAction: handleOnPressSaveChanges });

  if (rIsLoading) {
    return (
      <View style={[styles.flex1, styles.alignCenter, styles.justifyCenter]}>
        <ActivityIndicator size="large" color={kColors.brandDarkGreen} />
      </View>
    );
  }

  return (
    <View style={isMobile ? styles.mobilePage : styles.page}>
      <PdStack direction={isMobile ? "vertical" : "horizontal"} style={[styles.justifyBetween, styles.mb4]}>
        <PdText variant="subheader">Products</PdText>
        <PdStack direction={isMobile ? "vertical" : "horizontal"}>
          <PdButton
            onPress={handleOnAddProduct}
            colorScheme="green"
            leftIcon={<PdAdd style={styles.buttonIcon} />}
            isFullWidth={isMobile}
          >
            <PdButtonLabel>Product</PdButtonLabel>
          </PdButton>
          {!isMobile && (
            <PdButton
              onPress={handleOnImportProducts}
              colorScheme="gray"
              leftIcon={<PdImport style={styles.buttonIcon} />}
              isFullWidth={isMobile}
            >
              <PdButtonLabel>Import Products</PdButtonLabel>
            </PdButton>
          )}
          {rIsShopifyFeatureFlagOn && (
            <PdButton
              onPress={handleOnPressShopify}
              colorScheme="gray"
              leftIcon={<PdShopify style={styles.buttonIcon} />}
              isFullWidth={isMobile}
            >
              <PdButtonLabel>{rIsLinkedToShopify ? "Shopify Settings" : "Shopify Integration"}</PdButtonLabel>
            </PdButton>
          )}
        </PdStack>
      </PdStack>
      <View style={styles.mb4}>
        {!isMobile && <PdFormLabel>Search your products</PdFormLabel>}
        <PdSearchInput
          value={filters.term}
          onChangeText={(newTerm) => setFilters((prev) => ({ ...prev, term: newTerm }))}
        />
      </View>
      <View style={styles.filtersStatusContainer}>
        <PdTab isActive={!filters.status} onPress={() => setFilters((prev) => ({ ...prev, status: undefined }))}>
          <PdTabLabel>{`All (${totals.all})`}</PdTabLabel>
        </PdTab>
        <PdTab
          isActive={filters.status === tProductStatus.Published}
          onPress={() => setFilters((prev) => ({ ...prev, status: tProductStatus.Published }))}
        >
          <PdTabLabel>{`Published (${totals.published})`}</PdTabLabel>
        </PdTab>
        <PdTab
          isActive={filters.status === tProductStatus.Unpublished}
          onPress={() => setFilters((prev) => ({ ...prev, status: tProductStatus.Unpublished }))}
        >
          <PdTabLabel>{`Unpublished (${totals.unpublished})`}</PdTabLabel>
        </PdTab>
        <PdTab
          isActive={filters.status === tProductStatus.OutOfStock}
          onPress={() => setFilters((prev) => ({ ...prev, status: tProductStatus.OutOfStock }))}
        >
          <PdTabLabel>{`Out of Stock (${totals.outOfStock})`}</PdTabLabel>
        </PdTab>
      </View>
      <View style={styles.tableOptionsContainer}>
        <PdRow style={styles.tableOptions}>
          <PdCheckbox
            value={isPublishing}
            onChange={handleOnPressBulkPublish}
            isDisabled={!isSomeSelected}
            isLoading={isBulkPublishing}
          >
            <PdCheckboxLabel>Publish</PdCheckboxLabel>
          </PdCheckbox>
          <PdCheckbox
            value={isUnpublishing}
            onPress={handleOnPressBulkUnpublish}
            isDisabled={!isSomeSelected}
            isLoading={isBulkUnpublishing}
          >
            <PdCheckboxLabel>Unpublish</PdCheckboxLabel>
          </PdCheckbox>
          <PdCheckbox
            value={isMarkingOos}
            onPress={handleOnPressOutOfStock}
            isDisabled={!isSomeSelected}
            isLoading={isBulkMarkingOos}
          >
            <PdCheckboxLabel>Out of Stock</PdCheckboxLabel>
          </PdCheckbox>
        </PdRow>
        <PdRow style={styles.tableOptions}>
          {!isMobile && (
            <PdButton
              colorScheme="gray"
              size="sm"
              rightIcon={<PdCopy style={styles.iconSm} />}
              onPress={handleOnPressCopyAsCSV}
            >
              <PdButtonLabel>Copy as CSV</PdButtonLabel>
            </PdButton>
          )}
          {!isMobile && (
            <PdPopover offset={22 + 2 * kStyleConst.kSpacing}>
              <PdPopoverTrigger>
                <PdButton colorScheme="gray" size="sm" rightIcon={<PdSettings style={styles.iconSm} />}>
                  <PdButtonLabel>Configure Table</PdButtonLabel>
                </PdButton>
              </PdPopoverTrigger>
              <PdPopoverContent style={styles.configureTablePopoverContent}>
                <PopoverContentTableColumns
                  columns={columns}
                  tableConfigReduxKey="inventoryManagament"
                  onPressCheckbox={updateHiddenColumns}
                />
              </PdPopoverContent>
            </PdPopover>
          )}
          <PdButton
            size="sm"
            variant="outline"
            onPress={handleOnPressSaveChanges}
            isLoading={isSaving}
            isDisabled={!isSomeUpdated}
          >
            <PdButtonLabel>Save Changes</PdButtonLabel>
          </PdButton>
        </PdRow>
      </View>
      <PdTable
        ListHeaderComponent={<PdTableSortableHeaders headers={tableInstance.headers} />}
        data={tableInstance.rows}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => (
          <View>
            <PdTableRow>
              {item.cells.map((cell) => (
                <PdTableCell key={cell.uniqueId} style={cell.style}>
                  {cell.render("Cell")}
                </PdTableCell>
              ))}
            </PdTableRow>
            {item.children?.map((subRow) => {
              return (
                <PdTableRow key={subRow.id}>
                  {subRow.cells.map((subRowCell) => (
                    <PdTableCell key={subRowCell.uniqueId} style={subRowCell.style}>
                      {subRowCell.render("Cell")}
                    </PdTableCell>
                  ))}
                </PdTableRow>
              );
            })}
          </View>
        )}
      />
    </View>
  );
};

export const P08 = React.memo(P08_Products);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  alignEnd: {
    alignItems: "flex-end",
  },
  buttonIcon: {
    height: 11,
    width: 11,
  },
  configureTablePopoverContent: {
    backgroundColor: kColors.white,
    borderColor: kColors.gray300,
    borderRadius: 5,
    borderWidth: 1,
    left: -150,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingVertical: kStyleConst.kSpacing * 3,
    right: 0,
  },
  filtersStatusContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: kStyleConst.kSpacing * 2,
    marginBottom: kStyleConst.kSpacing * 4,
  },
  flex1: {
    flex: 1,
  },
  flexRow: {
    flexDirection: "row",
  },
  iconSm: {
    height: 11,
    width: 11,
  },
  image: {
    backgroundColor: kColors.lightMed,
    height: 30,
    width: 30,
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  justifyCenter: {
    justifyContent: "center",
  },
  maxW400: {
    maxWidth: 400,
  },
  mb10: {
    marginBottom: 10 * kStyleConst.kSpacing,
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  ml2: {
    marginLeft: kStyleConst.kSpacing * 2,
  },
  mobilePage: {
    flex: 1,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingVertical: kStyleConst.kSpacing * 4,
  },
  mr2: {
    marginRight: 2 * kStyleConst.kSpacing,
  },
  page: {
    flex: 1,
    paddingHorizontal: kStyleConst.kSpacing * 10,
    paddingVertical: kStyleConst.kSpacing * 8,
  },
  pdInputGroupInput: {
    borderWidth: 0,
  },
  rowHovered: {
    backgroundColor: kColors.gray50,
  },
  rowPressed: {
    opacity: 0.6,
  },
  sizeXxs: {
    height: 8,
    width: 8,
  },
  tableColumnLg: {
    width: 300,
  },
  tableColumnMd: {
    width: 160,
  },
  tableColumnSm: {
    width: 110,
  },
  tableColumnXl: {
    width: 500,
  },
  tableColumnXs: {
    width: 40,
  },
  tableOptions: {
    flexWrap: "wrap",
    gap: kStyleConst.kSpacing * 2,
  },
  tableOptionsContainer: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: kStyleConst.kSpacing * 2,
    justifyContent: "space-between",
    marginBottom: kStyleConst.kSpacing * 2,
    zIndex: 1,
  },
  widthFull: {
    width: "100%",
  },
});

const webStyles: { [key: string]: React.CSSProperties } = {
  overflowAuto: {
    overflow: "auto",
  },
  image: {
    backgroundColor: kColors.lightMed,
    height: 30,
    width: 30,
  },
} as const;
